import React, { useState, useCallback } from 'react';
import { Latam3DSService } from './latam.3ds.service';
import { Latam3DSScript } from './latam.3ds.script';
import type { Latam3DSConfig, Latam3DSData, Latam3DSCallbacks, Latam3DSResponse, Latam3DSServiceResponse } from './latam.3ds.types';
import { maskToken, logEnvironment, maskCardNumber } from './utils/latam.3ds.utils';

/**
 * Hook para integração com Latam Gateway 3DS
 */
export const useLatam3DS = (shouldLoad3DS: boolean = true) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  // ✅ FUNÇÃO GLOBAL: Logs para backend via endpoint 3DS - desabilitada (apenas console local)
  React.useEffect(() => {
    if (shouldLoad3DS && typeof window !== 'undefined') {
      (window as any).logToBackend = (message: string, status: 'loading' | 'success' | 'error') => {
        // Logs desabilitados - apenas console local
        console.log(`[${status.toUpperCase()}] ${message}`);
      };
    }
  }, [shouldLoad3DS]);

  // ============================================================
  // ✅ Passo 2: Obter Token 3DS
  // ============================================================
  const getToken = useCallback(async (): Promise<{ token: string; environment: string } | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const response: Latam3DSServiceResponse = await Latam3DSService.getCreditCardToken();

      if (response.success && response.token && response.environment) {
        setToken(response.token);
        logEnvironment('Environment recebido do backend', response.environment);

        // 🔹 Log padronizado
        console.log('🌍 Frontend 3DS - Token + Environment:', {
          environment: response.environment,
          token: maskToken(response.token),
        });

        return { token: response.token, environment: response.environment };
      }

      throw new Error(response.error || 'Falha ao obter token 3DS');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================================
  // ✅ Passo 3: Executar processo 3DS
  // ============================================================
  const process3DS = useCallback(async (config: Latam3DSConfig, data: Latam3DSData, callbacks: Latam3DSCallbacks): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      Latam3DSScript.logToBackend('Iniciando processo 3DS', 'loading');

      // 🔹 Extraí tempo de espera para constante
      const SCRIPT_LOAD_DELAY = 500;
      const scriptLoaded = await Latam3DSScript.loadScript();

      if (!scriptLoaded) {
        throw new Error('Falha ao carregar script 3DS - verifique a conexão com a internet');
      }

      await new Promise((resolve) => setTimeout(resolve, SCRIPT_LOAD_DELAY));

      if (!Latam3DSScript.isScriptLoaded()) {
        throw new Error('Objeto Latam não disponível após carregamento do script');
      }

      const latamObject = Latam3DSScript.getLatamObject();
      if (!latamObject) throw new Error('Objeto Latam não encontrado');

      Latam3DSScript.logToBackend('Script 3DS carregado - objeto disponível', 'success');

      // 🔹 Melhorado log de environment
      logEnvironment('Environment para latamInstance', config.environment, 'backend');
      console.log('🌍 Frontend 3DS - Environment configurado:', {
        environment: config.environment,
        latamEnvironment: latamObject.Environment[config.environment],
      });

      const latamInstance = {
        environment: latamObject.Environment[config.environment as keyof typeof latamObject.Environment],
        authorizationToken: config.authorizationToken,
        data,
        callbacks: {
          onSuccess: (response: any) => {
            Latam3DSScript.logToBackend('3DS processado com sucesso', 'success');

            const installmentsFromRequest = data?.credit_card?.installments ? Number(data.credit_card.installments) : undefined;

            const finalInstallments =
              response?.credit_card?.installments !== undefined ? Number(response.credit_card.installments) : installmentsFromRequest ?? 1;

            const responseWithInstallments = {
              ...response,
              credit_card: {
                ...(response?.credit_card || {}),
                installments: finalInstallments,
              },
            };

            // 🔹 Log mascarado simplificado
            console.log('3DS - onSuccess (mascarado):', {
              ...responseWithInstallments,
              credit_card: {
                ...responseWithInstallments.credit_card,
                card_number: maskCardNumber(responseWithInstallments?.credit_card?.card_number || ''),
                cvv: responseWithInstallments?.credit_card?.cvv ? '***' : undefined,
              },
              credentials: {
                payment_method_nonce: responseWithInstallments?.credentials?.payment_method_nonce
                  ? maskToken(responseWithInstallments.credentials.payment_method_nonce)
                  : undefined,
              },
            });

            callbacks.onSuccess(responseWithInstallments);
          },
          onError: (response: any) => {
            Latam3DSScript.logToBackend(
              `Erro no processamento 3DS: ${response?.error || response?.message || 'Erro desconhecido'}`,
              'error',
            );
            console.error('3DS - onError:', response);
            callbacks.onError(response);
          },
        },
      };

      const maskedToken = config.authorizationToken
        ? `${config.authorizationToken.slice(0, 8)}...${config.authorizationToken.slice(-4)}`
        : 'token inválido';

      Latam3DSScript.logToBackend(`Token 3DS usado: ${maskedToken}`, 'loading');

      const latamThreeDs = new latamObject.ThreeDSecure(latamInstance);
      latamThreeDs.process();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      Latam3DSScript.logToBackend(`Erro crítico ao executar 3DS: ${errorMessage}`, 'error');
      console.error('❌ Erro ao executar 3DS:', errorMessage);
      setError(errorMessage);

      callbacks.onError({ error: errorMessage, step: 'process_3ds' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================================
  // ✅ Passo 4: Processar pagamento final
  // ============================================================
  const processPayment = useCallback(async (latamId: string, paymentData: Latam3DSResponse): Promise<Latam3DSServiceResponse> => {
    setIsLoading(true);
    setError(null);

    Latam3DSScript.logToBackend(`Iniciando pagamento final - latamId: ${latamId}`, 'loading');

    try {
      const response = await Latam3DSService.processCreditCardPayment(latamId, paymentData);

      if (response.success) {
        Latam3DSScript.logToBackend(`Pagamento 3DS concluído - latamId: ${latamId}`, 'success');
        console.log('✅ Pagamento 3DS processado com sucesso');
      } else {
        Latam3DSScript.logToBackend(`Erro no pagamento 3DS: ${response.error || 'Erro desconhecido'}`, 'error');
        console.error('❌ Erro no pagamento 3DS:', response.error);
        setError(response.error || 'Erro no pagamento');
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      Latam3DSScript.logToBackend(`Erro crítico no pagamento 3DS: ${errorMessage}`, 'error');
      console.error('❌ Erro no pagamento 3DS:', errorMessage);
      setError(errorMessage);

      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ============================================================
  // ✅ Fluxo completo (Passos 2, 3 e 4)
  // ============================================================
  const executeFull3DSFlow = useCallback(
    async (
      orderId: string,
      customerData: any,
      creditCardData: any,
      addressData: any,
      orderValue: number,
      onSuccess: (result: any) => void,
      onError: (error: any) => void,
    ): Promise<void> => {
      setIsLoading(true);
      setError(null);

      // ✅ Retentativa: até 3 tentativas para erro "Life cycle"
      const maxAttempts = 3;
      const isLifeCycleError = (error: any): boolean => {
        const errorMsg = error?.error || error?.message || '';
        return errorMsg.toLowerCase().includes('life cycle') || errorMsg.toLowerCase().includes('cannot authorize at this time');
      };

      let lastError: any = null;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          if (attempt > 1) {
            Latam3DSScript.logToBackend(`🔄 Retentativa 3DS - Tentativa ${attempt}/${maxAttempts} - orderId: ${orderId}`, 'loading');
            // Aguarda 1 segundo antes de tentar novamente
            await new Promise(resolve => setTimeout(resolve, 1000));
          }

          Latam3DSScript.logToBackend(`Fluxo completo 3DS iniciado - orderId: ${orderId} - Tentativa ${attempt}/${maxAttempts}`, 'loading');

          const tokenData = await getToken();
          if (!tokenData) throw new Error('Falha ao obter token 3DS');

          const { token: authToken, environment: backendEnvironment } = tokenData;

          const maskedToken = authToken ? `${authToken.slice(0, 8)}...${authToken.slice(-4)}` : 'token inválido';

          Latam3DSScript.logToBackend(`Token 3DS obtido: ${maskedToken} - environment: ${backendEnvironment}`, 'success');

          const latamData = Latam3DSService.convertFormDataToLatam3DS(orderId, customerData, creditCardData, addressData, orderValue);

          Latam3DSScript.logToBackend(`Dados convertidos para formato Latam - orderId: ${orderId}`, 'success');

          const config: Latam3DSConfig = {
            environment: backendEnvironment as 'HOMOLOG' | 'PRODUCTION',
            authorizationToken: authToken,
          };

          // Executa 3DS com callback para retentativa se for erro Life cycle
          await new Promise<void>((resolve, reject) => {
            process3DS(config, latamData, {
              onSuccess: (response) => {
                Latam3DSScript.logToBackend(`Fluxo 3DS executado com sucesso - orderId: ${orderId}`, 'success');
                onSuccess(response);
                resolve();
              },
              onError: (error) => {
                const errorMessage = error?.error || error?.message || 'Erro desconhecido';
                lastError = error;

                // Se for erro Life cycle e ainda não é última tentativa, retenta
                if (isLifeCycleError(error) && attempt < maxAttempts) {
                  Latam3DSScript.logToBackend(
                    `⚠️ Erro Life cycle detectado (tentativa ${attempt}/${maxAttempts}) - aguardando para retentar...`,
                    'loading',
                  );
                  resolve(); // Resolve para retentar no loop
                } else {
                  // Erro final ou não é Life cycle
                  Latam3DSScript.logToBackend(
                    `Erro no fluxo 3DS - orderId: ${orderId}: ${errorMessage}`,
                    'error',
                  );
                  console.error('❌ Erro no fluxo 3DS:', error);
                  onError(error);
                  reject(error);
                }
              },
            });
          });

          // Se chegou aqui, sucesso!
          break;

        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
          lastError = { error: errorMessage };

          // Se não é erro Life cycle ou última tentativa, para aqui
          if (!isLifeCycleError(lastError) || attempt === maxAttempts) {
            Latam3DSScript.logToBackend(`Erro crítico no fluxo completo 3DS - orderId: ${orderId}: ${errorMessage}`, 'error');
            console.error('❌ Erro no fluxo completo 3DS:', errorMessage);
            setError(errorMessage);
            onError(lastError);
            break;
          }
        }
      }

      // Se esgotou todas as tentativas com erro Life cycle, retorna erro especial
      if (isLifeCycleError(lastError)) {
        Latam3DSScript.logToBackend(`❌ Erro Life cycle após ${maxAttempts} tentativas - orderId: ${orderId}`, 'error');
        onError({ 
          error: 'Erro no 3DS após múltiplas tentativas. Tentando gateway alternativo.',
          code: 'LIFE_CYCLE_MAX_RETRIES',
          lastAttempt: true 
        });
      }

      setIsLoading(false);
    },
    [getToken, process3DS],
  );

  // ============================================================
  // ✅ Reset do estado
  // ============================================================
  const reset = useCallback(() => {
    setError(null);
    setToken(null);
    setIsLoading(false);
  }, []);

  return {
    isLoading,
    error,
    token,
    getToken,
    process3DS,
    processPayment,
    executeFull3DSFlow,
    reset,
    hasToken: !!token,
    hasError: !!error,
  };
};
